import type { ChangeEvent } from 'react';
import React from 'react';
import type { Data, Fields } from '../../../types';
import type { Meta } from '../types';
import { TableSelect } from '../../../components/Selects/TableSelect';
import { TableMultiSelect } from '../../../components/Selects/TableMultiSelect';
import { compareOptions } from '../../../components/Selects/compareOptions';
import {
  Box,
  Grid,
  Icon,
  InputBase,
  styled,
  Tooltip,
  Checkbox,
  Switch,
  Typography
} from '@beeline.com/bp-design-system';
import { DEFAULT_MULTI_VALUE_SEPARATOR } from '../../MatchColumnsStep/utils/normalizeMultiValue';
import { stringUtil } from '../../MatchColumnsStep/utils/stringUtil';
import { type Column, useRowSelection } from '../../../ReactDataGrid';

const SELECT_COLUMN_KEY = 'select-row';

function autoFocusAndSelect(input: HTMLInputElement | null) {
  input?.focus();
  input?.select();
}

export const generateColumns = <T extends string>(
  fields: Fields<T>,
  visibleRows: Array<Data<T> & Meta>,
  selectedRows: ReadonlySet<string | number>
): Column<Data<T> & Meta>[] => [
  {
    key: SELECT_COLUMN_KEY,
    name: '',
    width: 45,
    minWidth: 45,
    maxWidth: 45,
    resizable: false,
    sortable: false,
    frozen: true,
    headerRenderer: ({ allRowsSelected, onAllRowsSelectionChange }): JSX.Element => {
      const visibleSelectedCount = visibleRows.reduce(
        (count, row) => (selectedRows.has(row.__index) ? count + 1 : count),
        0
      );
      const isIndeterminate = visibleSelectedCount > 0 && visibleSelectedCount < visibleRows.length;
      return (
        <Checkbox
          aria-label="Select all"
          dataTest="select-all-rows"
          name=""
          slotProps={{
            checkbox: {
              style: { padding: 0 },
              indeterminate: isIndeterminate
            }
          }}
          checked={allRowsSelected}
          style={{ marginTop: -1, marginLeft: 0 }}
          onChange={(event) => {
            onAllRowsSelectionChange((event.target as HTMLInputElement).checked);
          }}
        />
      );
    },
    cellClass: 'rdg-checkbox',
    formatter: (props): JSX.Element => {
      const [isRowSelected, onRowSelectionChange] = useRowSelection();
      return (
        <Checkbox
          aria-label="Select"
          dataTest="select-row"
          name=""
          slotProps={{
            checkbox: {
              style: {
                padding: 0
              }
            }
          }}
          checked={isRowSelected}
          style={{ marginTop: -1, marginLeft: 0 }}
          onChange={(event) => {
            onRowSelectionChange({
              row: props.row,
              checked: Boolean((event.target as HTMLInputElement).checked),
              isShiftClick: (event.nativeEvent as MouseEvent).shiftKey
            });
          }}
        />
      );
    }
  },
  ...fields.map(
    (column): Column<Data<T> & Meta> => ({
      key: column.key as string,
      name: column.label,
      minWidth: 150,
      resizable: true,
      headerRenderer: (): JSX.Element => (
        <Grid container gap={1} direction="row" alignItems="center" position="relative">
          <Box flex={1} overflow="hidden" textOverflow="ellipsis">
            {column.label}
            {column.description && (
              <Tooltip
                tooltipTargetProps={{ style: { verticalAlign: 'middle', marginLeft: 8 } }}
                placement="top"
                arrow
                title={column.description}
              >
                <Icon size={20} name="info" />
              </Tooltip>
            )}
          </Box>
        </Grid>
      ),
      editable: column.fieldType.type !== 'checkbox',
      editor: ({ row, onRowChange, onClose }): JSX.Element => {
        let component: JSX.Element;
        switch (column.fieldType.type) {
          case 'select':
            component = (
              <TableSelect
                value={column.fieldType.options.find((option) => option.value === (row[column.key as T] as string))}
                onChange={(value) => {
                  onRowChange({ ...row, [column.key as T]: value?.value }, true);
                }}
                options={column.fieldType.options}
                sort={column.fieldType.sort}
              />
            );
            break;
          case 'multiselect':
            component = (
              <TableMultiSelect
                value={column.fieldType.options.filter((option) =>
                  ((row[column.key as T] ?? []) as string[]).includes(option.value)
                )}
                onChange={(value) => {
                  onRowChange({ ...row, [column.key as T]: value?.map((it) => it.value) }, true);
                }}
                options={column.fieldType.options}
                sort={column.fieldType.sort}
              />
            );
            break;
          default:
            component = (
              <StyledInputBase
                autoFocus
                fullWidth
                inputRef={autoFocusAndSelect}
                value={row[column.key as T] as string}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  onRowChange({
                    ...row,
                    [column.key as T]: event.target.value
                  });
                }}
                onBlur={() => onClose(true)}
              />
            );
        }

        return component;
      },
      editorOptions: {
        editOnClick: true
      },
      formatter: ({ row, onRowChange }): JSX.Element => {
        let component;

        switch (column.fieldType.type) {
          case 'checkbox':
            component = (
              <Box
                display="flex"
                alignItems="center"
                height="100%"
                margin="0 -8px"
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <Switch
                  size="small"
                  aria-label={column.label as string}
                  checked={row[column.key as T] as boolean}
                  onChange={() => {
                    onRowChange({
                      ...row,
                      [column.key as T]: !row[column.key as T]
                    });
                  }}
                />
              </Box>
            );
            break;
          case 'select':
            component = (
              <Box minWidth="100%" minHeight="100%" overflow="hidden" textOverflow="ellipsis">
                {column.fieldType.options.find((option) => option.value === row[column.key as T])?.label || null}
              </Box>
            );
            break;
          case 'multiselect':
            const values = row[column.key as T] as string[] | undefined;
            const displayValueArray = column.fieldType.options
              .filter((option) => values?.includes(option.value))
              ?.map((o) => stringUtil.unEscapedToEscaped(o.label));

            const sort = column.fieldType.sort ?? true;
            const displayValue = (sort ? displayValueArray.sort(compareOptions) : displayValueArray).join(
              `${column.fieldType.valueSeparator ?? DEFAULT_MULTI_VALUE_SEPARATOR} `
            );
            component = (
              <Box minWidth="100%" minHeight="100%" overflow="hidden" textOverflow="ellipsis" title={displayValue}>
                {displayValue || null}
              </Box>
            );
            break;
          default:
            component = (
              <Box minWidth="100%" minHeight="100%" overflow="hidden" textOverflow="ellipsis">
                {row[column.key as T]}
              </Box>
            );
        }

        if (row.__errors?.[column.key as T]?.level === 'warning') {
          return (
            <Box position="relative" width="100%">
              {component}
              <Tooltip
                placement="top"
                arrow
                title={<Typography>{row.__errors?.[column.key as T]?.message}</Typography>}
              >
                <StyledIcon name="warning" size={16} color="paletteAmber70" />
              </Tooltip>
            </Box>
          );
        }

        if (row.__errors?.[column.key as T]) {
          return (
            <Tooltip
              placement="top"
              arrow
              title={
                <Typography color="colorOnInformational" fontSize={16}>
                  {row.__errors?.[column.key as T]?.message}
                </Typography>
              }
            >
              {component}
            </Tooltip>
          );
        }

        return component;
      },
      cellClass: (row: Meta) => {
        switch (row.__errors?.[column.key as T]?.level) {
          case 'error':
            return 'rdg-cell-error';
          case 'warning':
            return 'rdg-cell-warning';
          case 'info':
            return 'rdg-cell-info';
          default:
            return '';
        }
      }
    })
  )
];

const StyledInputBase = styled(InputBase)`
  font-size: 14px;
  padding: 0;
  margin-top: -3px;
  background-color: transparent;
`;

const StyledIcon = styled(Icon)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;
